import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import HtmlEditor from "aldoo-ra/HtmlEditor"
import { showNotification } from "aldoo-ra/Notification"
import { AssetCRUD } from "./api"
import { confirmDelete } from "./Dashboard/KPITiles"
import inlineCSS from "./inline-css"

/** Local {{var}} substitution for the live preview pane. */
const substitute = (html, vars = {}) =>
  (html || "").replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, k) =>
    vars[k] === undefined || vars[k] === null ? "" : String(vars[k]))

/**
 * Asset editor — HTML banner with {{variables}} from the parent ad
 * (MailTemplates model). On save the markup is passed through the CSS
 * inliner so the stored asset is fully self-contained (utility classes
 * resolved to inline styles) and safe to serve on any partner site.
 */
const AssetEditor = ({ ad, asset, onClose, onSaved }) => {
  const [name, setName] = useState(asset?.name || "")
  const [html, setHtml] = useState(asset?.html || "")
  const [enabled, setEnabled] = useState(asset?.enabled !== false)
  const [inlineStyles, setInlineStyles] = useState(asset?.inline_styles === true)
  const [htmlOpen, setHtmlOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  //variables offered in the editor = the ad's variable keys
  const variableData = Object.keys(ad?.variables || {}).map((key) => ({ key, type: "text" }))

  const remove = () =>
    confirmDelete(asset?.name || "asset", async () => {
      await AssetCRUD.SoftDelete({ _id: asset._id })
      showNotification({ message: "Asset deleted", className: "bg-admin_success text-admin_text_inv" })
      onSaved?.()
    })

  const save = async () => {
    if (!name.trim()) {
      showNotification({ message: "Asset needs a name", className: "bg-admin_error text-admin_text_inv" })
      return
    }
    setSaving(true)
    try {
      // Default: store the markup as authored (the target site supplies the
      // classes). Opt-in: bake computed styles so it renders standalone.
      const outHtml = inlineStyles ? inlineCSS(html) : html
      const data = { ad: ad._id, name: name.trim(), html: outHtml, enabled, inline_styles: inlineStyles }
      if (asset?._id) await AssetCRUD.Update({ _id: asset._id, ...data })
      else await AssetCRUD.Create(data)
      showNotification({ message: "Asset saved", className: "bg-admin_success text-admin_text_inv" })
      onSaved?.()
    } catch (e) {
      console.error("Asset save failed", e)
      showNotification({ message: "Failed to save asset", className: "bg-admin_error text-admin_text_inv" })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      closeOnEscape
      variant="admin_compact"
      title={asset?._id ? "Edit asset" : "New asset"}
    >
      <div className="p-5 flex flex-col gap-4">

        <label className="text-sm text-admin_text_secondary">Name</label>
        <input
          className="border border-admin_bordered rounded-lg px-3 py-2 bg-transparent"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="home-page banner"
        />

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled (served when the ad + campaign are active)
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" className="mt-1" checked={inlineStyles} onChange={(e) => setInlineStyles(e.target.checked)} />
          <span>
            Inline Styles
            <span className="block text-xs text-admin_text_secondary">
              Off: serve the HTML as authored (the target site provides the classes, e.g. Tailwind).
              On: bake computed styles into the markup so it renders standalone — only for sites
              without the source classes.
            </span>
          </span>
        </label>

        <div className="flex items-center justify-between">
          <Typography variant="admin_h4">Banner HTML</Typography>
          <Button variant="admin_secondary" onClick={() => setHtmlOpen(true)}>
            {html ? "Edit HTML" : "Add HTML"}
          </Button>
        </div>

        {/* live preview with the ad's variables substituted */}
        {html && (
          <div className="border border-admin_bordered rounded-lg p-4 bg-white">
            <div dangerouslySetInnerHTML={{ __html: substitute(html, ad?.variables) }} />
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {asset?._id
            ? <Button variant="admin_error" onClick={remove}>Delete</Button>
            : <span />}
          <div className="flex gap-2">
            <Button variant="admin_secondary" onClick={onClose}>Cancel</Button>
            <Button variant="admin_primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save asset"}
            </Button>
          </div>
        </div>
      </div>

      {htmlOpen && (
        <Modal
          isOpen
          onClose={() => setHtmlOpen(false)}
          closeOnEscape
          variant="admin_fullScreen"
          size="fullscreen"
          styleOverrides={{
            container: "bg-admin_canvas w-full h-full overflow-y-auto",
            contentWrapper: "overflow-y-auto h-[calc(100vh-80px)]",
          }}
        >
          <HtmlEditor
            title={name || "Ad asset"}
            content={html}
            save={(content) => { setHtml(content); setHtmlOpen(false) }}
            variables={{ data: variableData }}
          />
        </Modal>
      )}
    </Modal>
  )
}

export default AssetEditor
